import { ReactNode } from 'react';
import { convertNumberPrice } from '../../../utils/FormatUtils';
import IsOwnExpensesCell from './cells/IsOwnExpensesCell';
import ObjectTypeCell from './cells/ObjectTypeCell';
import PaymentInsPercentCell from './cells/PaymentInsPercentCell';
import PaymentPtPercentCell from './cells/PaymentPtPercentCell';
import SvcTotalPaymentCell from './cells/SvcTotalPaymentCell';

const formatCellPrice = (value: string | number | null | undefined) => {
	// Return '0' for null/undefined/empty values
	if (value === null || value === undefined || value === '') return '0';

	// Format the value
	const formatedValue = convertNumberPrice(value);
	return formatedValue || '0';
};

const disableEditCell = (rowData: any, isTaoPhieu: boolean, isEditInvoiceMode: boolean = false) => {
	// Allow editing in both create mode (isTaoPhieu) and edit mode (isEditInvoiceMode)
	// But NEVER allow editing grouped parent rows (they are UI grouping only, not real data)
	// User must expand and edit individual child rows
	if (rowData?.isGroupParent) return true;
	return !(isTaoPhieu || isEditInvoiceMode) || rowData?.isPaid || rowData.isCancel;
};

const disableOwnExpensesCell = (rowData: any) => {
	// Own expenses checkbox can ONLY be toggled if:
	// 1. NOT a group parent row
	// 2. Row is NOT paid
	// 3. Row is NOT cancelled
	// Always allow toggling regardless of create/edit mode
	if (rowData?.isGroupParent) return true;
	if (rowData?.isPaid || rowData?.isCancel) return true;
	return false; // Always enabled (not disabled)
};

const EditableCellHeader = ({ children }: { children: ReactNode }) => {
	return (
		<span className="d-inline-flex align-items-center spaces gap-2">
			<span>{children}</span>
			<i className="bi bi-pencil-fill text-white fs-8"></i>
		</span>
	);
};

const getColumnThongTinVienPhi = (
	isPhieuThu: boolean,
	handleChangeValueTable: (
		row: any,
		value: any,
		index: any,
		name: any,
		previousValue: any
	) => void,
	arraySelect?: any,
	isTaoPhieu = false,
	termObjectTypes?: any[],
	selectedCell?: any,
	setSelectedCell?: any,
	isEditInvoiceMode = false,
	selectedOrderDataIds: string[] = [],
	handleToggleOrderData?: (orderDataId: string) => void,
	invoiceOrderDataIds: string[] = []
) => {
	const columns: any[] = [
		{
			title: 'Dịch vụ',
			field: 'termSvcNameClone',
			className: 'spaces w-25',
		},
		{
			title: 'ĐVT',
			field: 'termUnitName',
			className: 'spaces width-22px',
			render: (rowData: any) => {
				return (
					<span className={`${rowData?.isCancel ? 'text-danger' : ''}`}>
						{rowData?.termUnitName}
					</span>
				);
			},
		},
		{
			title: <EditableCellHeader>Đối tượng</EditableCellHeader>,
			field: 'termObjectTypeName',
			className: 'spaces width-38px text-center px-0',
			render: (rowData: any, rowIndex: number, cellIndex: number) => {
				return (
					<ObjectTypeCell
						rowData={rowData}
						rowIndex={rowIndex}
						cellIndex={cellIndex}
						onCellEdit={handleChangeValueTable}
						selectOptions={termObjectTypes ?? []}
						editable={!disableEditCell(rowData, isTaoPhieu, isEditInvoiceMode)}
						selectedCell={selectedCell}
						setSelectedCell={setSelectedCell}
					/>
				);
			},
		},
		{
			title: 'SL',
			field: 'svcQuantity',
			className: 'spaces width-20px text-center',
			render: (rowData: any) => {
				return (
					<span className={`${rowData?.isCancel ? 'text-danger' : ''}`}>
						{rowData?.svcQuantity}
					</span>
				);
			},
		},
		{
			title: 'Đơn giá',
			field: 'svcPriceClone',
			className: 'spaces width-35px text-end',
			render: (rowData: any) => {
				return (
					<span className={`${rowData?.isCancel ? 'text-danger' : ''}`}>
						{rowData?.id ? convertNumberPrice(rowData?.svcPriceClone) : '0'}
					</span>
				);
			},
		},
		{
			title: <EditableCellHeader>% TT</EditableCellHeader>,
			field: 'svcTotalPercent',
			className: 'spaces width-22px text-center px-0',
			render: (rowData: any, rowIndex: number, cellIndex: number) => {
				return (
					<SvcTotalPaymentCell
						rowData={rowData}
						rowIndex={rowIndex}
						cellIndex={cellIndex}
						onCellEdit={handleChangeValueTable}
						editable={!disableEditCell(rowData, isTaoPhieu, isEditInvoiceMode)}
						selectedCell={selectedCell}
						setSelectedCell={setSelectedCell}
					/>
				);
			},
		},
		{
			title: 'Thành tiền',
			field: 'svcTotalClone',
			className: 'spaces width-35px text-end',
			render: (rowData: any) => {
				return (
					<span className={`${rowData?.isCancel ? 'text-danger' : ''}`}>
						{rowData?.id ? convertNumberPrice(rowData?.svcTotalClone || rowData?.svcTotal) : '0'}
					</span>
				);
			},
		},
		{
			title: <EditableCellHeader>% MG</EditableCellHeader>,
			field: 'paymentPtPercent',
			className: 'spaces width-25px text-center px-0',
			render: (rowData: any, index: any, stt: number) => {
				return (
					<PaymentPtPercentCell
						rowData={rowData}
						rowIndex={index}
						onCellEdit={handleChangeValueTable}
						selectedCell={selectedCell}
						setSelectedCell={setSelectedCell}
						editable={
							isPhieuThu &&
							!rowData?.isPaid &&
							!rowData?.isGroupParent &&
							rowData?.svcPriceClone !== 0 &&
							(arraySelect?.includes(rowData?.id) || (isEditInvoiceMode && selectedOrderDataIds?.includes(rowData?.id)))
						}
					/>
				);
			},
		},
		{
			title: 'MG',
			field: 'discountClone',
			className: 'spaces width-30px text-end',
			render: (rowData: any, index: any, stt: number) => {
				return (
					<span className={`${rowData?.isCancel ? 'text-danger' : ''}`}>
						{formatCellPrice(rowData?.discountClone || rowData?.discount)}
					</span>
				);
			},
		},
		{
			title: <EditableCellHeader>% BH</EditableCellHeader>,
			field: 'paymentInsPercent',
			className: 'spaces width-22px text-center px-0',
			render: (rowData: any, rowIndex: number, cellIndex: number) => {
				return (
					<PaymentInsPercentCell
						rowData={rowData}
						rowIndex={rowIndex}
						cellIndex={cellIndex}
						onCellEdit={handleChangeValueTable}
						editable={!disableEditCell(rowData, isTaoPhieu, isEditInvoiceMode)}
						selectedCell={selectedCell}
						setSelectedCell={setSelectedCell}
					/>
				);
			},
		},
		{
			title: 'Thành tiền BHYT',
			field: 'totalIns',
			className: 'spaces width-35px text-end',
			render: (rowData: any, index: any, stt: number) => {
				return (
					<span className={`${rowData?.isCancel ? 'text-danger' : ''}`}>
						{formatCellPrice(rowData?.totalIns)}
					</span>
				);
			},
		},
		{
			title: 'Quỹ BHYT',
			field: 'paymentInsTotal',
			className: 'spaces width-35px text-end',
			render: (rowData: any, index: any, stt: number) => {
				return (
					<span className={`${rowData?.isCancel ? 'text-danger' : ''}`}>
						{formatCellPrice(rowData?.paymentInsTotal)}
					</span>
				);
			},
		},
		{
			title: '#bncct',
			field: 'bncct',
			className: 'spaces width-35px text-end',
			render: (rowData: any, index: any, stt: number) => {
				return (
					<span className={`${rowData?.isCancel ? 'text-danger' : ''}`}>
						{formatCellPrice(rowData?.paymentInsPtTotal)}
					</span>
				);
			},
		},
		{
			title: '#BNTT',
			field: 'bntt',
			className: 'spaces width-35px text-end',
			render: (rowData: any) => {
				return (
					<span className={`${rowData?.isCancel ? 'text-danger' : ''}`}>
						{formatCellPrice(rowData?.paymentPt)}
					</span>
				);
			},
		},
		{
			title: 'BN trả',
			field: 'paymentPtTotal',
			className: 'spaces width-35px text-end',
			render: (rowData: any) => {
				return (
					<span className={`${rowData?.isCancel ? 'text-danger' : ''}`}>
						{rowData?.id ? formatCellPrice(rowData?.paymentPtTotalClone) : '0'}
					</span>
				);
			},
		},
		{
			title: '#Đã thu BNCCT',
			field: 'daThuCCT',
			className: 'spaces width-35px text-end',
			render: (rowData: any) => {
				return (
					<span className={`${rowData?.isCancel ? 'text-danger' : ''}`}>
						{formatCellPrice(rowData?.paymentPtReceiptIns)}
					</span>
				);
			},
		},
		{
			title: '#Đã thu BNTT',
			field: 'daThubntt',
			className: 'spaces width-35px text-end',
			render: (rowData: any) => {
				return (
					<span className={`${rowData?.isCancel ? 'text-danger' : ''}`}>
						{formatCellPrice(rowData?.paymentPtReceipt)}
					</span>
				);
			},
		},
		{
			title: 'Đã thu',
			field: 'svcCollected',
			className: 'spaces width-35px text-end',
			render: (rowData: any) => {
				return (
					<span className={`${rowData?.isCancel ? 'text-danger' : ''}`}>
						{formatCellPrice(rowData?.svcCollected)}
					</span>
				);
			},
		},
		{
			title: 'Nguồn khác',
			field: 'svcPaymentSource',
			className: 'spaces width-35px text-end',
			render: (rowData: any) => {
				return (
					<span className={`${rowData?.isCancel ? 'text-danger' : ''}`}>
						{formatCellPrice(rowData?.svcPaymentSource)}
					</span>
				);
			},
		},
		{
			title: 'Giá chênh',
			field: 'svcFeeDifference',
			className: 'spaces width-35px text-end',
			render: (rowData: any) => {
				return (
					<span className={`${rowData?.isCancel ? 'text-danger' : ''}`}>
						{formatCellPrice(rowData?.svcFeeDifference)}
					</span>
				);
			},
		},
		{
			title: <EditableCellHeader>Tự túc</EditableCellHeader>,
			field: 'isOwnExpenses',
			className: 'spaces width-35px text-center px-0',
			render: (rowData: any, rowIndex: number, cellIndex: number) => {
				return (
					<IsOwnExpensesCell
						rowData={rowData}
						rowIndex={rowIndex}
						cellIndex={cellIndex}
						onCellEdit={handleChangeValueTable}
						editable={!disableOwnExpensesCell(rowData)}
						selectedCell={selectedCell}
						setSelectedCell={setSelectedCell}
					/>
				);
			},
		}
	];

	return columns;
};

export { getColumnThongTinVienPhi };
